import { useState } from 'react';
import {
  makeStyles, tokens, Title2, Title3, Body1, Caption1, Spinner, MessageBar, MessageBarBody, Badge,
  Table, TableHeader, TableRow, TableHeaderCell, TableBody, TableCell,
} from '@fluentui/react-components';
import { BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { ProjectPicker } from '../../../components/ProjectPicker';
import { useBugAging } from '../../../api/hooks';

// FEAT-020 Bug Aging. Stacked bars by age band × severity (Sev 1-4). Detail table below
// shows individual aging bugs Sev1 → Sev4, days desc.
const useStyles = makeStyles({
  page: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalL, height: '100%' },
  hint: { color: tokens.colorNeutralForeground2 },
  toolbar: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS },
  chartCard: {
    height: '20rem',
    padding: tokens.spacingHorizontalL,
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusMedium,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  tableWrapper: { flex: 1, overflow: 'auto', border: `1px solid ${tokens.colorNeutralStroke2}`, borderRadius: tokens.borderRadiusMedium },
});

function sevLabel(s: number | null): string {
  if (s === 1) return '1-Critical';
  if (s === 2) return '2-High';
  if (s === 3) return '3-Medium';
  if (s === 4) return '4-Low';
  return '—';
}

function sevColor(s: number | null) {
  if (s === 1) return 'danger' as const;
  if (s === 2) return 'warning' as const;
  return 'subtle' as const;
}

export function BugAgingPage() {
  const styles = useStyles();
  const [projectId, setProjectId] = useState<string | undefined>();
  const { data, isLoading, error } = useBugAging(projectId);

  return (
    <div className={styles.page}>
      <div className={styles.toolbar}>
        <Title2>Bug Aging</Title2>
        <Body1 className={styles.hint}>Open Bugs by age band and Severity. Stale Sev 1–2 Bugs are the ones to act on first.</Body1>
        <ProjectPicker selectedProjectId={projectId} onSelect={setProjectId} />
      </div>

      {!projectId ? (
        <Body1>Pick a project.</Body1>
      ) : isLoading ? (
        <Spinner label="Loading open bugs…" />
      ) : error ? (
        <MessageBar intent="error"><MessageBarBody>{(error as Error).message}</MessageBarBody></MessageBar>
      ) : !data || data.bugs.length === 0 ? (
        <Body1>No open bugs in this project. 🎉</Body1>
      ) : (
        <>
          <div className={styles.chartCard}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.buckets}>
                <XAxis dataKey="ageBand" />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Legend />
                <Bar dataKey="severity1" stackId="a" fill={tokens.colorPaletteRedBackground3} name="Sev 1" />
                <Bar dataKey="severity2" stackId="a" fill={tokens.colorPaletteDarkOrangeBackground3} name="Sev 2" />
                <Bar dataKey="severity3" stackId="a" fill={tokens.colorPaletteYellowBackground3} name="Sev 3" />
                <Bar dataKey="severity4" stackId="a" fill={tokens.colorPaletteBlueBackground2} name="Sev 4" />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <Title3>Open bugs</Title3>
          <div className={styles.tableWrapper}>
            <Table aria-label="Open bugs by severity and age" size="small">
              <TableHeader>
                <TableRow>
                  <TableHeaderCell>ID</TableHeaderCell>
                  <TableHeaderCell>Title</TableHeaderCell>
                  <TableHeaderCell>Severity</TableHeaderCell>
                  <TableHeaderCell>State</TableHeaderCell>
                  <TableHeaderCell>Days open</TableHeaderCell>
                  <TableHeaderCell>Created</TableHeaderCell>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.bugs.map((b) => (
                  <TableRow key={b.id}>
                    <TableCell><Caption1>#{b.id}</Caption1></TableCell>
                    <TableCell>{b.title}</TableCell>
                    <TableCell><Badge appearance="filled" color={sevColor(b.severity)}>{sevLabel(b.severity)}</Badge></TableCell>
                    <TableCell><Caption1>{b.state}</Caption1></TableCell>
                    <TableCell>{b.daysOpen}</TableCell>
                    <TableCell><Caption1>{new Date(b.createdDate).toLocaleDateString()}</Caption1></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </>
      )}
    </div>
  );
}
